'use client'

import { useEffect, useRef, useState } from 'react'

/**
 * IntersectionObserver-basiertes Reveal. Triggert exakt einmal beim ersten
 * Sichtbarwerden des Elements. Respektiert prefers-reduced-motion.
 */
export function Reveal({
  children,
  as: As = 'div',
  delay = 0,
  slow = false,
  className = '',
  threshold = 0.2,
  once = true,
  ...rest
}: {
  children: React.ReactNode
  as?: keyof React.JSX.IntrinsicElements
  delay?: 0 | 1 | 2 | 3 | 4 | 5
  slow?: boolean
  className?: string
  threshold?: number
  once?: boolean
} & React.HTMLAttributes<HTMLElement>) {
  const ref = useRef<HTMLElement | null>(null)
  // R100.57 (2026-05-30): Initial `true` wenn der User reduced-motion bevorzugt
  // ODER auf einer langen Page mit >50 Reveal-Wrappern (Legal-Pages). Statt
  // 79× IntersectionObserver wird der Inhalt direkt sichtbar — keine
  // 300+ Transitions-of-all gleichzeitig mehr.
  const [seen, setSeen] = useState(() => {
    if (typeof window === 'undefined') return false
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return true
    return false
  })

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (seen) return // bereits sichtbar (reduced-motion) — kein Observer nötig
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setSeen(true)
            if (once) io.unobserve(e.target)
          } else if (!once) {
            setSeen(false)
          }
        })
      },
      { threshold, rootMargin: '0px 0px -10% 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [threshold, once, seen])

  const cls = [
    'award-reveal',
    slow ? 'award-reveal-slow' : '',
    delay ? `award-reveal-delayed-${delay}` : '',
    seen ? 'is-in' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const Comp = As as any
  return (
    <Comp ref={ref as any} className={cls} {...rest}>
      {children}
    </Comp>
  )
}
